/**
 * Reine Helper für die Spalten-Einstellungen pro User (Sichtbarkeit und
 * Reihenfolge). Kein React, keine Netzwerkzugriffe, damit die Logik ohne
 * Rendering testbar bleibt.
 *
 * Gespeichert wird ein Eintrag im Array Space.customer_dashboard_meta:
 * { id: 'UI_TABLE_SETTINGS', version: 1, tables: { talents: { hidden, shown, order, updated_at }, ... } }
 *
 * Nur anpassbare Spalten (meta.customizable) landen in hidden und order.
 * Strukturelle Spalten (select, view, Filterträger) und die Primärspalte
 * (meta.primary) werden nie geschrieben und beim Lesen herausgefiltert.
 *
 * Optionale Spalten (meta.optional, z. B. die Zusatzfelder aus dem
 * Einladungsformular) sind standardmäßig ausgeblendet. Für sie merkt sich der
 * Eintrag nicht hidden, sondern shown. Nur gezeigte optionale Spalten landen in
 * order, alle übrigen bleiben aus der Reihenfolge draußen.
 *
 * Der Draft trennt bekannte von unbekannten Ids (bedingte Spalten wie
 * careers.external_custom_id, entfernte Spalten, Zusatzfelder vor dem Laden). Unbekannte Ids behalten in
 * storedOrder ihren Index, damit eine Spalte nach ihrer Rückkehr wieder an der
 * gemerkten Position steht.
 */

export const TABLE_IDS = { TALENTS: 'talents', CAREERS: 'careers' }
export const TABLE_SETTINGS_META_ID = 'UI_TABLE_SETTINGS'
const TABLE_SETTINGS_VERSION = 1

// Gemeinsame Quelle für Seiten und Settings: welche Spalten sind anpassbar und
// wie heißen sie. Die Spaltendefinitionen holen ihr meta über customizableMeta,
// die Partner-Einstellung listet dieselben Ids.
export const TABLE_COLUMN_CATALOG = {
  [TABLE_IDS.TALENTS]: [
    { id: 'assessment_id', labelKey: 'talents.column_label_career' },
    { id: 'progress', labelKey: 'talents.column_label_progress' },
    { id: 'status', labelKey: 'talents.column_label_status' },
    { id: 'score', labelKey: 'talents.column_label_score' },
    { id: 'updatedAt', labelKey: 'talents.column_label_last_activity' },
    { id: 'created_status', labelKey: 'talents.column_label_created_at' }
  ],
  [TABLE_IDS.CAREERS]: [
    {
      id: 'external_custom_id',
      labelKey: 'pages.careers.table_header_career_external_id'
    },
    {
      id: 'number_of_spaces',
      labelKey: 'pages.careers.table_header_career_number_of_spaces'
    },
    {
      id: 'updatedAt',
      labelKey: 'pages.careers.table_header_career_updated_at'
    },
    {
      id: 'createdAt',
      labelKey: 'pages.careers.table_header_career_created_at'
    },
    { id: 'status', labelKey: 'pages.careers.table_header_career_status' },
    {
      id: 'is_available_in_attract',
      labelKey: 'pages.careers.table_header_career_is_available_in_attract'
    }
  ]
}

export const customizableMeta = (tableId, id, t) => {
  const entry = (TABLE_COLUMN_CATALOG[tableId] || []).find(
    (item) => item.id === id
  )

  return { label: entry ? t(entry.labelKey) : id, customizable: true }
}

// Partner-Ebene: dashboard_settings.table_columns[tableId].disabled sind Ids,
// die für alle Nutzer des Partners deaktiviert sind. Nutzer können sie weder
// sehen noch einblenden. Default: nichts deaktiviert.
export const PARTNER_TABLE_COLUMNS_KEY = 'table_columns'

export const readPartnerTableColumns = (dashboardSettings) => {
  const raw =
    dashboardSettings && typeof dashboardSettings === 'object'
      ? dashboardSettings[PARTNER_TABLE_COLUMNS_KEY]
      : null

  return Object.fromEntries(
    Object.keys(TABLE_COLUMN_CATALOG).map((tableId) => {
      const catalogIds = new Set(
        TABLE_COLUMN_CATALOG[tableId].map((item) => item.id)
      )
      const disabled = toIdList(raw?.[tableId]?.disabled).filter((id) =>
        catalogIds.has(id)
      )

      return [tableId, disabled]
    })
  )
}

const MAX_IDS = 50

const isPlainObject = (value) =>
  !!value && typeof value === 'object' && !Array.isArray(value)

// Nur nicht-leere Strings, dedupliziert, gekappt. Alles andere fällt weg.
const toIdList = (value) => {
  if (!Array.isArray(value)) return []

  const seen = new Set()
  const result = []

  for (const item of value) {
    if (typeof item !== 'string' || !item || seen.has(item)) continue
    seen.add(item)
    result.push(item)
    if (result.length >= MAX_IDS) break
  }

  return result
}

const getColumnDefId = (def) => def.id ?? def.accessorKey

const isCustomizable = (def) =>
  def.meta?.customizable === true &&
  def.meta?.primary !== true &&
  def.enableHiding !== false &&
  !def.invisible

const isOptional = (def) => isCustomizable(def) && def.meta?.optional === true

export const getColumnRegistry = (columns) => {
  const customizableIds = []
  const optionalIds = []
  const labelsById = {}
  let primary = null

  columns.forEach((def) => {
    const id = getColumnDefId(def)
    if (!id) return

    if (def.meta?.label) labelsById[id] = def.meta.label
    if (def.meta?.primary === true && !primary) {
      primary = { id, label: def.meta.label || id }
    }
    if (isCustomizable(def)) customizableIds.push(id)
    if (isOptional(def)) optionalIds.push(id)
  })

  return { customizableIds, optionalIds, primary, labelsById }
}

// User-Reihenfolge der anpassbaren Spalten: bekannte Ids in gespeicherter
// Reihenfolge (dedupliziert), danach alle übrigen in Definitionsreihenfolge.
export const orderCustomizable = (userOrder, customizableIds) => {
  const known = new Set(customizableIds)
  const ordered = userOrder.filter(
    (id, index) => known.has(id) && userOrder.indexOf(id) === index
  )
  const rest = customizableIds.filter((id) => !ordered.includes(id))

  return [...ordered, ...rest]
}

// Vollständige react-table columnOrder: strukturelle Spalten bleiben an ihrer
// Definitionsposition, die anpassbaren Slots werden mit der User-Reihenfolge
// gefüllt. select bleibt so immer vorne, view immer hinten.
export const resolveColumnOrder = (columns, userOrder) => {
  const customizableIds = columns.filter(isCustomizable).map(getColumnDefId)
  const customizable = new Set(customizableIds)
  const ordered = orderCustomizable(userOrder, customizableIds)

  let slot = 0

  return columns
    .map(getColumnDefId)
    .filter(Boolean)
    .map((id) => (customizable.has(id) ? ordered[slot++] : id))
}

// hidden/shown/order: nur aktuell bekannte Ids (fürs Rendering).
// hiddenUnknown/shownUnknown/storedOrder: tragen unbekannte Ids samt Position weiter.
export const EMPTY_DRAFT = {
  hidden: [],
  hiddenUnknown: [],
  shown: [],
  shownUnknown: [],
  order: [],
  storedOrder: []
}

// Anpassbare Ids, die gerade in der Liste stehen: alle regulären und die
// optionalen, die der Nutzer hinzugefügt hat. Definitionsreihenfolge.
export const getActiveIds = (draft, customizableIds, optionalIds = []) => {
  const optional = new Set(optionalIds)

  return customizableIds.filter(
    (id) => !optional.has(id) || draft.shown.includes(id)
  )
}

// Optionale Spalte hinzufügen (hinten an die Liste) oder wieder entfernen.
// Beim Entfernen verschwindet sie auch aus der Reihenfolge, damit ein
// abgewähltes Zusatzfeld keine Anpassung hinterlässt.
export const toggleOptional = (draft, id, customizableIds, optionalIds) => {
  if (draft.shown.includes(id)) {
    const drop = (ids) => ids.filter((other) => other !== id)

    return {
      ...draft,
      shown: drop(draft.shown),
      order: drop(draft.order),
      storedOrder: drop(draft.storedOrder)
    }
  }

  const next = { ...draft, shown: [...draft.shown, id] }
  const before = orderCustomizable(
    draft.order,
    getActiveIds(draft, customizableIds, optionalIds)
  )

  return withOrder(
    next,
    [...before, id],
    getActiveIds(next, customizableIds, optionalIds)
  )
}

// Liest den Tabellen-Eintrag aus dem normalisierten Meta-Array. Liefert null,
// wenn nichts gespeichert ist oder die Form nicht passt. Wirft nie.
export const readTableSettings = (metaArray, tableId) => {
  const entry = (Array.isArray(metaArray) ? metaArray : []).find(
    (item) => isPlainObject(item) && item.id === TABLE_SETTINGS_META_ID
  )

  if (!entry) return null
  if ((entry.version ?? TABLE_SETTINGS_VERSION) !== TABLE_SETTINGS_VERSION) {
    return null
  }
  if (!isPlainObject(entry.tables)) return null

  const table = entry.tables[tableId]
  if (!isPlainObject(table)) return null

  return {
    hidden: toIdList(table.hidden),
    shown: toIdList(table.shown),
    order: toIdList(table.order)
  }
}

// stored -> Draft. Unbekannte Ids werden nicht verworfen, sondern getrennt
// mitgeführt und beim Schreiben an ihrer Position wieder eingesetzt.
export const normalizeStored = (stored, customizableIds) => {
  if (!stored) return EMPTY_DRAFT

  const known = new Set(customizableIds)
  const hidden = toIdList(stored.hidden)
  const shown = toIdList(stored.shown)
  const order = toIdList(stored.order)

  return {
    hidden: hidden.filter((id) => known.has(id)),
    hiddenUnknown: hidden.filter((id) => !known.has(id)),
    shown: shown.filter((id) => known.has(id)),
    shownUnknown: shown.filter((id) => !known.has(id)),
    order: order.filter((id) => known.has(id)),
    storedOrder: order
  }
}

// Draft gegen eine neue Spaltenmenge neu aufteilen (bedingte Spalte taucht
// auf oder verschwindet). Läuft über storedOrder, damit eine zurückkehrende
// Spalte ihre gemerkte Position behält.
export const renormalizeDraft = (draft, customizableIds) =>
  normalizeStored(
    {
      hidden: [...draft.hidden, ...draft.hiddenUnknown],
      shown: [...draft.shown, ...draft.shownUnknown],
      order: draft.storedOrder.length ? draft.storedOrder : draft.order
    },
    customizableIds
  )

// Neue bekannte Reihenfolge in die gespeicherte Liste einweben: bekannte
// Slots werden in der neuen Reihenfolge befüllt, unbekannte Ids bleiben an
// ihrem Index, neue bekannte Ids kommen hinten dran.
export const mergeOrder = (storedOrder, knownOrder, customizableIds) => {
  const known = new Set(customizableIds)
  const queue = orderCustomizable(knownOrder, customizableIds)
  const merged = []

  toIdList(storedOrder).forEach((id) => {
    if (!known.has(id)) {
      merged.push(id)
      return
    }

    const next = queue.shift()
    if (next) merged.push(next)
  })

  return [...merged, ...queue]
}

// Draft mit neuer User-Reihenfolge, storedOrder wird konsistent mitgezogen.
export const withOrder = (draft, nextOrder, customizableIds) => {
  const order = orderCustomizable(nextOrder, customizableIds)

  return {
    ...draft,
    order,
    storedOrder: mergeOrder(draft.storedOrder, order, customizableIds)
  }
}

// Draft -> stored. storedOrder ist führend, neue bekannte Ids werden angehängt.
export const draftToStored = (draft, customizableIds) => {
  const stored = toIdList(draft.storedOrder)
  const storedSet = new Set(stored)

  const shown = [...draft.shown, ...draft.shownUnknown]

  // shown nur schreiben, wenn es etwas gibt. Einträge ohne Zusatzfelder
  // behalten so ihre bisherige Form.
  return {
    hidden: [...draft.hidden, ...draft.hiddenUnknown],
    ...(shown.length ? { shown } : {}),
    order: [
      ...stored,
      ...orderCustomizable(draft.order, customizableIds).filter(
        (id) => !storedSet.has(id)
      )
    ]
  }
}

export const isDefaultSettings = (draft, customizableIds) => {
  if (draft.hidden.length || draft.hiddenUnknown.length) return false
  if (draft.shown.length || draft.shownUnknown.length) return false

  const known = new Set(customizableIds)
  if (draft.storedOrder.some((id) => !known.has(id))) return false

  const ordered = orderCustomizable(draft.order, customizableIds)

  return ordered.every((id, index) => id === customizableIds[index])
}

// Ersetzt den Eintrag mit der Id oder entfernt ihn (entryOrNull === null).
// Alle anderen Einträge bleiben unverändert.
export const upsertMetaEntry = (metaArray, id, entryOrNull) => {
  const rest = (Array.isArray(metaArray) ? metaArray : []).filter(
    (item) => !(isPlainObject(item) && item.id === id)
  )

  return entryOrNull ? [...rest, entryOrNull] : rest
}

// Baut den neuen UI_TABLE_SETTINGS-Eintrag. Liefert null, wenn danach keine
// Tabelle mehr gespeichert ist (dann verschwindet der Eintrag komplett).
export const buildEntry = (
  metaArray,
  tableId,
  draft,
  customizableIds,
  now = new Date().toISOString()
) => {
  const existing = (Array.isArray(metaArray) ? metaArray : []).find(
    (item) => isPlainObject(item) && item.id === TABLE_SETTINGS_META_ID
  )

  // Ein Eintrag einer neueren Version wird nicht angefasst.
  if (
    existing &&
    (existing.version ?? TABLE_SETTINGS_VERSION) !== TABLE_SETTINGS_VERSION
  ) {
    return existing
  }

  const tables = isPlainObject(existing?.tables) ? { ...existing.tables } : {}

  if (isDefaultSettings(draft, customizableIds)) {
    delete tables[tableId]
  } else {
    tables[tableId] = {
      ...draftToStored(draft, customizableIds),
      updated_at: now
    }
  }

  if (!Object.keys(tables).length) return null

  return { id: TABLE_SETTINGS_META_ID, version: TABLE_SETTINGS_VERSION, tables }
}
